/**
 * "Best fits in your current view" — the right column of `scheduling-main.png`.
 *
 * The score is shown with its reasons, always. A ranking nobody can argue with
 * is a ranking nobody trusts: a scheduler who can see "same zone, 6.5 h open,
 * has dump trailer" can tell when the ranking is wrong about their week.
 */
import { useEffect, useState } from 'react'
import { Button, Card, SectionLabel, Tag } from './primitives.js'
import { formatDateOnly, formatNumber, parseDateOnly } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type { BestFit, QueueItem } from '../lib/schedule-types.js'

export function BestFitPanel({
  item, from, to, editable, busy, onAssign, onClose,
}: {
  item: QueueItem
  from: string
  to: string
  editable?: boolean
  busy?: boolean
  onAssign: (fit: BestFit) => void
  onClose: () => void
}) {
  const [fits, setFits] = useState<BestFit[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setFits(null)
    const query = new URLSearchParams({ workItemId: item.workItemId, from, to })
    request<{ fits: BestFit[] }>(`/schedule/best-fits?${query.toString()}`)
      .then((result) => {
        if (cancelled) return
        setFits(result.fits)
        setChosen(result.fits[0] ? `${result.fits[0].crewId}|${result.fits[0].date}` : null)
      })
      .catch(() => { if (!cancelled) setFits([]) })
    return () => { cancelled = true }
  }, [item.workItemId, from, to])

  const selected = fits?.find((fit) => `${fit.crewId}|${fit.date}` === chosen) ?? null

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-2 border-b border-line px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-card font-semibold text-text">{item.propertyName}</p>
          <p className="m-0 text-sub text-text-muted">
            {item.jobTitle} · {item.jobReference}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-text-muted hover:text-text"
        >
          ×
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <Card className="mb-3 p-3">
          <dl className="m-0 space-y-1">
            <Row label="Needs" value={
              <>
                {formatNumber(item.remainingHours)} h
                {item.requires.length > 0 && (
                  <> · {item.requires.map((need) => <Tag key={need}>{need}</Tag>)}</>
                )}
              </>
            } />
            <Row label="Window" value={
              item.earliestOn && item.dueOn
                ? `${formatDateOnly(item.earliestOn)} – ${formatDateOnly(item.dueOn)}`
                : item.dueOn ? `By ${formatDateOnly(item.dueOn)}` : 'Any time'
            } />
            <Row label="Zone" value={item.zoneName ?? 'Unzoned'} />
            {item.scheduledHours > 0 && (
              <Row
                label="Already placed"
                value={`${formatNumber(item.scheduledHours)} of ${formatNumber(item.budgetedHours)} h`}
              />
            )}
          </dl>
        </Card>

        <SectionLabel className="mb-2 block">Best fits in your current view</SectionLabel>

        {fits === null && <p className="m-0 text-cell text-text-muted">Looking…</p>}

        {fits?.length === 0 && (
          <p className="m-0 text-cell text-text-muted">
            No crew in this view has open hours and the gear this needs. Widen the dates, or
            change the filters.
          </p>
        )}

        <div className="flex flex-col gap-2">
          {fits?.map((fit) => {
            const key = `${fit.crewId}|${fit.date}`
            return (
              <button
                key={key}
                type="button"
                onClick={() => setChosen(key)}
                className={cn(
                  'flex gap-2.5 rounded-card border p-2.5 text-left',
                  key === chosen
                    ? 'border-accent bg-accent-soft'
                    : 'border-line hover:border-line-strong',
                )}
              >
                <span className={cn('tnum shrink-0 text-kpi',
                  fit.score >= 80 ? 'text-accent' : fit.score >= 60 ? 'text-text' : 'text-warn')}>
                  {fit.score}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-cell font-semibold text-text">
                    {fit.crewName} · {weekday(fit.date)}
                  </span>
                  <span className="block text-sub text-text-muted">
                    {fit.reasons.join(' · ')}
                  </span>
                  {fit.overrunHours > 0 && (
                    <span className="block text-sub text-warn">
                      {formatNumber(fit.overrunHours)} h would still need another day
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {editable && selected && (
        <div className="border-t border-line p-3">
          <Button
            variant="primary"
            className="w-full"
            disabled={busy}
            onClick={() => onAssign(selected)}
          >
            {busy
              ? 'Scheduling…'
              : `Assign to ${selected.crewName} · ${weekday(selected.date)}`}
          </Button>
          {selected.overrunHours > 0 && (
            <p className="m-0 mt-1.5 text-sub text-text-faint">
              This places {formatNumber(selected.openHours)} h. The rest stays in the queue.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-[92px] shrink-0 text-cell text-text-muted">{label}</dt>
      <dd className="m-0 min-w-0 flex-1 text-cell text-text">{value}</dd>
    </div>
  )
}

const weekday = (iso: string): string => {
  const date = parseDateOnly(iso)
  return date
    ? date.toLocaleDateString('en-US',
      { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    : iso
}
